<template>
  <section id="location-config" class="page-section location-section">
    <div class="location-section-background" aria-hidden="true">
      <div v-show="locationType === 'radius'" class="location-map-layer">
        <div class="map-container" ref="mapContainer"></div>
      </div>
      <div v-show="locationType === 'geojson'" class="location-map-layer">
        <div class="map-container" ref="geoJsonMapContainer"></div>
      </div>
    </div>

    <button
      class="location-map-theme-toggle"
      type="button"
      :data-scheme="mapColorScheme"
      :aria-label="mapThemeToggleLabel"
      :aria-pressed="mapColorScheme === MAP_COLOR_SCHEME_DARK"
      @click="toggleMapColorScheme"
    ></button>

      <div class="location-content">
      <div class="location-overlay">
        <!-- Mode Toggle -->
        <p class="zone-picker-label">Pick Your Zone</p>
        <div class="mode-toggle">
          <button 
            class="toggle-btn" 
            :class="{ active: locationType === 'radius' }"
            @click="setMode('radius')"
          >
            Radius
          </button>
          <button 
            class="toggle-btn" 
            :class="{ active: locationType === 'geojson' }"
            @click="setMode('geojson')"
          >
            Geo Fence
          </button>
        </div>

        <!-- Radius Mode -->
        <div v-show="locationType === 'radius'" class="radius-mode" :aria-hidden="locationType !== 'radius'">
          <div class="search-container radius-search">
            <div
              class="search-input-group form-field"
              :class="{ 'search-input-group--active': locationType === 'radius' }"
              :data-location-mode="locationType"
            >
              <input 
                type="text" 
                v-model="radiusSearchQuery" 
                placeholder="Search for an address"
                @keyup.enter="searchRadiusLocation"
                class="search-input"
                aria-label="Search for an address to place a radius pin"
              >
              <button class="search-btn" @click="searchRadiusLocation" :disabled="isSearching" aria-label="Run address search">
                {{ isSearching ? 'Searching...' : 'Search' }}
              </button>
            </div>
          </div>

          <div class="map-controls map-controls--radius">
            <div class="controls controls--vertical">
              <div class="control-group">
                <div class="radius-display">
                  <span class="radius-label">Radius:</span>
                  <span class="radius-value">{{ formattedRadius }}</span>
                </div>
                <div class="slider-container slider-container--vertical">
                  <div class="radius-notches">
                    <div 
                      v-for="notch in RADIUS_STOPS" 
                      :key="notch.v" 
                      class="radius-notch"
                      :style="{ bottom: notch.p + '%' }"
                      @click="setRadius(notch.v)"
                      role="button"
                      :aria-label="'Set radius to ' + notch.label"
                      tabindex="0"
                    >
                      <span class="notch-line"></span>
                      <span class="notch-label">{{ notch.label }}</span>
                    </div>
                  </div>
                  <input 
                    class="radius-slider"
                    type="range" 
                    v-model.number="rawSliderValue" 
                    min="0" 
                    max="100" 
                    step="0.1"
                    @input="onSliderInput"
                    @change="onSliderChange"
                    aria-label="Adjust campaign radius"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- GeoJSON Mode -->
        <div v-show="locationType === 'geojson'" class="geojson-mode" :aria-hidden="locationType !== 'geojson'">
          <!-- Search Option -->
          <div class="search-container geojson-search">
            <div
              class="search-input-group form-field"
              :class="{ 'search-input-group--active': locationType === 'geojson' }"
              :data-location-mode="locationType"
            >
              <input 
                type="text" 
                v-model="searchQuery" 
                placeholder="Search for a location"
                @keyup.enter="searchLocation"
                class="search-input"
                aria-label="Search for a location"
              >
              <button class="search-btn" @click="searchLocation" :disabled="isSearching" aria-label="Run location search">
                {{ isSearching ? 'Searching...' : 'Search' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Results Modal -->
    <LocationSearchModal 
      :show="showResultsModal" 
      :results="modalResults"
      @close="showResultsModal = false"
      @select="handleModalSelect"
    />
  </section>
</template>

<script setup lang="ts">
import { ref, shallowRef, onMounted, onUnmounted, watch, computed } from 'vue'
import maplibregl from 'maplibre-gl'
import type { Map, Marker, LngLatBounds, PaddingOptions, StyleSpecification } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { withIdentityPoolId } from '@aws/amazon-location-utilities-auth-helper'
import { useCampaignBuilderStore } from '@/store/campaignBuilder.store'
import { useUIStore } from '@/store/ui.store'
import { ApiService } from '@/services/api.service'
import type { GeoSearchResult } from '@/services/api.service'
import type { LocationType, LocationConfig } from '@/types/form.types'
import type {
  GeoJSONFeature,
  GeoJSONFeatureCollection,
  GeoJSONGeometry,
  LngLat
} from '@/types/geojson.types'
import LocationSearchModal, { type UnifiedSearchResult } from '@/components/LocationSearchModal.vue'

// Search types
/** Properties returned by the Places search API */
interface NominatimProperties {
  display_name: string
  type: string
  place_id: number | string
  osm_id?: number | string
  osm_type?: string
  class?: string
  [key: string]: unknown
}

/** A GeoJSON feature specifically from the Places API with expected property fields */
interface NominatimFeature extends GeoJSONFeature {
  properties: NominatimProperties
  display_name?: string
  place_id?: number | string
}

const DEFAULT_LOCATION_LABEL = 'Selected Location'
const DEFAULT_LOCATION_TYPE = 'location'
const DEFAULT_PLACE_ID = 0
const DEFAULT_OSM_ID = 'unknown'
const DEFAULT_OSM_TYPE = 'unknown'

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

const normalizePlaceId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const normalizeOsmId = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'string' && value.trim()) return value.trim()
  return null
}

const normalizeOptionalString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value.trim()
  return null
}

const resolveGeoJsonOsmId = (collection: GeoJSONFeatureCollection | null): string => {
  if (!collection?.features?.length) return DEFAULT_OSM_ID
  const properties = collection.features[0]?.properties
  if (!isRecord(properties)) return DEFAULT_OSM_ID
  const normalized = normalizeOsmId(properties.osm_id)
  return normalized ?? DEFAULT_OSM_ID
}

const resolveGeoJsonOsmType = (collection: GeoJSONFeatureCollection | null): string => {
  if (!collection?.features?.length) return DEFAULT_OSM_TYPE
  const properties = collection.features[0]?.properties
  if (!isRecord(properties)) return DEFAULT_OSM_TYPE
  const normalized = normalizeOptionalString(properties.osm_type)
  return normalized ?? DEFAULT_OSM_TYPE
}

const resolveGeoJsonClass = (collection: GeoJSONFeatureCollection | null): string | undefined => {
  if (!collection?.features?.length) return undefined
  const properties = collection.features[0]?.properties
  if (!isRecord(properties)) return undefined
  const normalized = normalizeOptionalString(properties.class)
  return normalized ?? undefined
}

const resolveDisplayName = (result: GeoSearchResult): string => {
  const name = result.name?.trim()
  const displayName = result.display_name?.trim()
  if (name && displayName) {
    const lowerDisplay = displayName.toLowerCase()
    const lowerName = name.toLowerCase()
    if (lowerDisplay.startsWith(lowerName)) return displayName
    return `${name} — ${displayName}`
  }
  return name || displayName || DEFAULT_LOCATION_LABEL
}

type StandardFeatureProperties = Record<string, unknown> & {
  display_name: string
  type: string
  place_id: number
  osm_id?: string
  osm_type?: string
  class?: string
}

const buildStandardProperties = (
  base: Record<string, unknown> | undefined,
  overrides: {
    display_name?: string
    type?: string
    place_id?: number | string
    osm_id?: number | string
    osm_type?: string
    class?: string
  }
): StandardFeatureProperties => {
  const baseRecord = base ?? {}
  const displayName =
    overrides.display_name ??
    (typeof baseRecord.display_name === 'string' ? baseRecord.display_name : DEFAULT_LOCATION_LABEL)
  const type =
    overrides.type ?? (typeof baseRecord.type === 'string' ? baseRecord.type : DEFAULT_LOCATION_TYPE)
  const placeIdValue = normalizePlaceId(overrides.place_id ?? baseRecord.place_id)
  const osmIdValue = normalizeOsmId(overrides.osm_id ?? baseRecord.osm_id)
  const osmTypeValue = normalizeOptionalString(overrides.osm_type ?? baseRecord.osm_type)
  const classValue = normalizeOptionalString(overrides.class ?? baseRecord.class)

  return {
    ...baseRecord,
    display_name: displayName,
    type,
    // Backend requires numeric place_id; default to 0 if unavailable.
    place_id: placeIdValue ?? DEFAULT_PLACE_ID,
    ...(osmIdValue ? { osm_id: osmIdValue } : {}),
    ...(osmTypeValue ? { osm_type: osmTypeValue } : {}),
    ...(classValue ? { class: classValue } : {})
  }
}

const mapFeatureCollectionResult = (feature: GeoJSONFeature): NominatimFeature | null => {
  if (!isGeoJsonGeometry(feature.geometry)) return null

  const rawProps = isRecord(feature.properties) ? feature.properties : {}
  const placeIdCandidate = rawProps.place_id
  const osmIdCandidate = rawProps.osm_id
  const osmTypeCandidate = rawProps.osm_type
  const classCandidate = rawProps.class
  const normalizedProps = buildStandardProperties(rawProps, {
    display_name: typeof rawProps.display_name === 'string' ? rawProps.display_name : undefined,
    type: typeof rawProps.type === 'string' ? rawProps.type : undefined,
    place_id:
      typeof placeIdCandidate === 'string' || typeof placeIdCandidate === 'number'
        ? placeIdCandidate
        : undefined,
    osm_id:
      typeof osmIdCandidate === 'string' || typeof osmIdCandidate === 'number'
        ? osmIdCandidate
        : undefined,
    osm_type: typeof osmTypeCandidate === 'string' ? osmTypeCandidate : undefined,
    class: typeof classCandidate === 'string' ? classCandidate : undefined
  })

  return {
    ...feature,
    type: 'Feature',
    display_name: normalizedProps.display_name,
    place_id: normalizedProps.place_id,
    properties: normalizedProps
  }
}

const isGeoJsonFeatureCollection = (value: unknown): value is GeoJSONFeatureCollection => {
  if (!isRecord(value)) return false
  if (value.type !== 'FeatureCollection') return false
  return Array.isArray(value.features)
}

const normalizeGeoJsonFeature = (
  feature: GeoJSONFeature,
  overrides: {
    display_name?: string
    type?: string
    place_id?: number | string
    osm_id?: number | string
    osm_type?: string
    class?: string
  }
): GeoJSONFeature | null => {
  const geometryType = feature.geometry.type
  if (geometryType !== 'Polygon' && geometryType !== 'MultiPolygon') return null

  const properties = buildStandardProperties(feature.properties, overrides)

  return {
    ...feature,
    geometry: feature.geometry,
    properties
  }
}

const normalizeGeoJsonCollection = (
  collection: GeoJSONFeatureCollection
): GeoJSONFeatureCollection | null => {
  const polygonFeatures = collection.features
    .map((feature) => normalizeGeoJsonFeature(feature, {}))
    .filter((feature): feature is GeoJSONFeature => Boolean(feature))
  if (!polygonFeatures.length) return null
  return {
    type: 'FeatureCollection',
    features: polygonFeatures
  }
}

const normalizeGeoJsonSelection = (
  feature: GeoJSONFeature,
  fallback: {
    display_name?: string
    type?: string
    place_id?: number | string
    osm_id?: number | string
    osm_type?: string
    class?: string
  }
): GeoJSONFeatureCollection | null => {
  const normalized = normalizeGeoJsonFeature(feature, fallback)
  if (!normalized) return null
  return {
    type: 'FeatureCollection',
    features: [normalized]
  }
}

const isGeoSearchResult = (value: unknown): value is GeoSearchResult => {
  if (!isRecord(value)) return false
  if (typeof value.display_name !== 'string') return false
  return true
}

const isGeoSearchResultArray = (value: unknown): value is GeoSearchResult[] => {
  return Array.isArray(value) && value.every(isGeoSearchResult)
}


// Props & Emits
defineEmits(['continue'])

// Store
const store = useCampaignBuilderStore()
const uiStore = useUIStore()

// State
/** The current location selection mode: 'radius' for a circular area or 'geojson' for a custom area */
const locationType = ref<LocationType>('radius')

const RADIUS_MIN = 100
const RADIUS_MAX = 150000

/** The radius in meters for the circular campaign area (used when locationType is 'radius') */
const radius = ref(RADIUS_MAX)

const RADIUS_STOPS = [
  { p: 0, v: 100, label: '100m' },
  { p: 25, v: 1000, label: '1km' },
  { p: 50, v: 10000, label: '10km' },
  { p: 75, v: 50000, label: '50km' },
  { p: 100, v: 150000, label: '150km' }
]

const sliderToRadius = (p: number) => {
  const upper = RADIUS_STOPS.find((s) => s.p >= p) || RADIUS_STOPS[RADIUS_STOPS.length - 1]
  const lower = [...RADIUS_STOPS].reverse().find((s) => s.p <= p) || RADIUS_STOPS[0]
  if (upper.p === lower.p) return upper.v
  const factor = (p - lower.p) / (upper.p - lower.p)
  const val = lower.v + factor * (upper.v - lower.v)
  if (val < 1000) return Math.round(val / 10) * 10
  if (val < 10000) return Math.round(val / 100) * 100
  if (val < 50000) return Math.round(val / 500) * 500
  return Math.round(val / 1000) * 1000
}

const radiusToSlider = (v: number) => {
  const clamped = Math.max(RADIUS_MIN, Math.min(RADIUS_MAX, v))
  const upper = RADIUS_STOPS.find((s) => s.v >= clamped) || RADIUS_STOPS[RADIUS_STOPS.length - 1]
  const lower = [...RADIUS_STOPS].reverse().find((s) => s.v <= clamped) || RADIUS_STOPS[0]
  const lowerV = lower.v
  const upperV = upper.v
  if (upperV === lowerV) return upper.p
  const factor = (clamped - lowerV) / (upperV - lowerV)
  return lower.p + factor * (upper.p - lower.p)
}

const formattedRadius = computed(() => {
  if (radius.value >= 1000) {
    return (radius.value / 1000).toFixed(radius.value % 1000 === 0 ? 0 : 1) + 'km'
  }
  return radius.value + 'm'
})

/** Temporary slider binding to avoid updating the map until the user releases the thumb */
const rawSliderValue = ref(radiusToSlider(radius.value))

const onSliderInput = () => {
  radius.value = sliderToRadius(rawSliderValue.value)
}

const onSliderChange = () => {
  updateCircle()
}

const setRadius = (v: number) => {
  radius.value = v
  rawSliderValue.value = radiusToSlider(v)
  updateCircle()
}

/** The central latitude and longitude coordinates for the circular campaign area */
const center = ref<{ lat: number; lng: number } | null>(null)

/** DOM reference to the map container element for radius mode */
const mapContainer = ref<HTMLElement | null>(null)

/** 
 * The Mapbox GL Map instance for radius mode.
 * Uses shallowRef to prevent Vue from trying to make the massive Map object deeply reactive,
 * which causes performance issues and TypeScript "deep instantiation" errors.
 */
const map = shallowRef<Map | null>(null)

/** 
 * The Mapbox GL Marker instance representing the center of the radius area.
 * Uses shallowRef for consistency and to avoid unnecessary reactivity overhead.
 */
const marker = shallowRef<Marker | null>(null)

/** Reference to the imported maplibregl library for dynamic usage */
let MapLibreLib: typeof maplibregl | null = null
let radiusNavControlAdded = false
let geoJsonNavControlAdded = false

type MapAuthHelper = Awaited<ReturnType<typeof withIdentityPoolId>>

/** Cached auth helper to avoid re-initialization */
const mapAuthHelper = ref<MapAuthHelper | null>(null)

// GeoJSON State
/** The GeoJSON data structure representing the selected custom campaign area */
const geoJsonData = ref<GeoJSONFeatureCollection | null>(null)

/** The filename of the uploaded or selected GeoJSON location */
const geoJsonFileName = ref('')

/** The file size in bytes of the uploaded GeoJSON location */
const geoJsonFileSize = ref(0)

/** DOM reference to the map container element for GeoJSON mode */
const geoJsonMapContainer = ref<HTMLElement | null>(null)

/** 
 * The Mapbox GL Map instance for GeoJSON preview and selection.
 * Uses shallowRef to avoid deep reactivity overhead and TypeScript recursion errors.
 */
const geoJsonMap = shallowRef<Map | null>(null)

const MAPLIBRE_FALLBACK_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    openstreetmap: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }
  },
  layers: [
    {
      id: 'osm-base',
      type: 'raster',
      source: 'openstreetmap'
    }
  ]
}
const AWS_REGION = import.meta.env.VITE_AWS_REGION
const AWS_IDENTITY_POOL_ID = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID
const AWS_LOCATION_API_KEY = import.meta.env.VITE_AWS_LOCATION_API_KEY
const DEFAULT_MAP_STYLE_NAME = 'Standard'
const AWS_LOCATION_STYLE_NAME =
  import.meta.env.VITE_AWS_LOCATION_STYLE_NAME ||
  import.meta.env.VITE_AWS_LOCATION_STYLE ||
  DEFAULT_MAP_STYLE_NAME
const AWS_LOCATION_BASE_URL = AWS_REGION ? `https://maps.geo.${AWS_REGION}.amazonaws.com` : ''
const MAP_COLOR_SCHEME_DARK = 'Dark' as const
const MAP_COLOR_SCHEME_LIGHT = 'Light' as const
type MapColorScheme = typeof MAP_COLOR_SCHEME_DARK | typeof MAP_COLOR_SCHEME_LIGHT
const DEFAULT_MAP_COLOR_SCHEME: MapColorScheme = MAP_COLOR_SCHEME_DARK
const MAP_COLOR_SCHEME_LABELS: Record<MapColorScheme, string> = {
  [MAP_COLOR_SCHEME_DARK]: 'Dark',
  [MAP_COLOR_SCHEME_LIGHT]: 'Light'
}
const mapColorScheme = ref<MapColorScheme>(DEFAULT_MAP_COLOR_SCHEME)
const mapThemeToggleLabel = computed(() => {
  const nextScheme =
    mapColorScheme.value === MAP_COLOR_SCHEME_DARK ? MAP_COLOR_SCHEME_LIGHT : MAP_COLOR_SCHEME_DARK
  return `Switch map style to ${MAP_COLOR_SCHEME_LABELS[nextScheme]} mode`
})
const DEFAULT_MAP_CENTER = { lat: 51.507351, lng: -0.127758 } // Fallback when GeoIP fails
const DEFAULT_MAP_ZOOM = 3.5
const GEO_IP_ENDPOINT = 'https://ipapi.co/json/'
const LAT_RANGE = { min: -90, max: 90 }
const LNG_RANGE = { min: -180, max: 180 }
const MAP_CENTER_VERTICAL_RATIO = 0.38
const GEOJSON_CENTER_VERTICAL_RATIO = 0.3
const MAP_FIT_BASE_PADDING = 50
/** The address string persisted for the selected point (geocoding removed) */
const selectedAddress = ref('')
const approximateCenter = ref(DEFAULT_MAP_CENTER)
const hasFallenBackToPublicMap = ref(false)

const isValidLatLng = (lat: number, lng: number) => {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= LAT_RANGE.min &&
    lat <= LAT_RANGE.max &&
    lng >= LNG_RANGE.min &&
    lng <= LNG_RANGE.max
  )
}

// Keep the target location lower in the viewport to preserve UI visibility.
const buildMapCenterPadding = (
  container: HTMLElement | null,
  verticalRatio: number = MAP_CENTER_VERTICAL_RATIO
): PaddingOptions => {
  if (!container) return { top: 0, bottom: 0, left: 0, right: 0 }
  const height = container.clientHeight
  if (!Number.isFinite(height) || height <= 0) return { top: 0, bottom: 0, left: 0, right: 0 }
  const offsetRatio = Math.max(0, (2 * verticalRatio) - 1)
  const top = Math.round(height * offsetRatio)
  return { top, bottom: 0, left: 0, right: 0 }
}

const buildMapFitPadding = (
  container: HTMLElement | null,
  verticalRatio: number = MAP_CENTER_VERTICAL_RATIO
): PaddingOptions => {
  const centerPadding = buildMapCenterPadding(container, verticalRatio)
  return {
    top: centerPadding.top + MAP_FIT_BASE_PADDING,
    bottom: MAP_FIT_BASE_PADDING,
    left: MAP_FIT_BASE_PADDING,
    right: MAP_FIT_BASE_PADDING
  }
}

const applyMapCenterPadding = (
  mapInstance: Map | null,
  container: HTMLElement | null,
  verticalRatio: number = MAP_CENTER_VERTICAL_RATIO
) => {
  if (!mapInstance) return
  mapInstance.setPadding(buildMapCenterPadding(container, verticalRatio))
}

const resolveAwsLocationStyleUrl = (scheme: MapColorScheme) => {
  if (!AWS_REGION || !AWS_LOCATION_STYLE_NAME) return ''
  const baseUrl = `${AWS_LOCATION_BASE_URL}/v2/styles/${AWS_LOCATION_STYLE_NAME}/descriptor`
  const url = new URL(baseUrl)
  url.searchParams.set('color-scheme', scheme)
  if (AWS_LOCATION_API_KEY) {
    url.searchParams.set('key', AWS_LOCATION_API_KEY)
  }
  return url.toString()
}

const appendApiKeyToLocationRequest = (requestUrl: string) => {
  if (!AWS_LOCATION_API_KEY) return requestUrl

  try {
    const url = new URL(requestUrl)
    if (!url.hostname.endsWith('.amazonaws.com')) return requestUrl
    if (!url.hostname.startsWith('maps.geo.')) return requestUrl
    if (!url.searchParams.has('key')) {
      url.searchParams.set('key', AWS_LOCATION_API_KEY)
    }
    return url.toString()
  } catch {
    return requestUrl
  }
}

const resolveMapOptions = async () => {
  const styleUrl = resolveAwsLocationStyleUrl(mapColorScheme.value)
  if (!styleUrl) {
    return { style: MAPLIBRE_FALLBACK_STYLE }
  }

  if (!AWS_IDENTITY_POOL_ID && !AWS_LOCATION_API_KEY) {
    return { style: MAPLIBRE_FALLBACK_STYLE }
  }

  if (AWS_IDENTITY_POOL_ID) {
    try {
      if (!mapAuthHelper.value) {
        mapAuthHelper.value = await withIdentityPoolId(AWS_IDENTITY_POOL_ID, {
          region: AWS_REGION
        } as unknown as Record<string, string>)
      }

      const authOptions = mapAuthHelper.value.getMapAuthenticationOptions()
      const originalTransform =
        authOptions.transformRequest ?? ((url: string) => ({ url }))

      return {
        style: styleUrl,
        ...authOptions,
        transformRequest: (url: string, resourceType?: string) => {
          const result = originalTransform(url, resourceType)
          const resultWithHeaders = result as { url: string; headers?: Record<string, string> }
          return result
        }
      }
    } catch (error) {
      console.error('Failed to initialize AWS Location auth helper:', error)
    }
  }

  if (AWS_LOCATION_API_KEY) {
    return {
      style: styleUrl,
      transformRequest: (url: string) => ({
        url: appendApiKeyToLocationRequest(url)
      })
    }
  }

  return { style: MAPLIBRE_FALLBACK_STYLE }
}

const waitForStyleReady = (mapInstance: Map, onReady: () => void) => {
  if (mapInstance.isStyleLoaded()) {
    onReady()
    return
  }

  const handleStyleData = () => {
    if (!mapInstance.isStyleLoaded()) {
      mapInstance.once('styledata', handleStyleData)
      return
    }
    onReady()
  }

  mapInstance.once('styledata', handleStyleData)
}

const applyMapStyle = (mapInstance: Map | null, scheme: MapColorScheme, onReady: () => void) => {
  if (!mapInstance) return
  const styleUrl = resolveAwsLocationStyleUrl(scheme)
  mapInstance.setStyle(styleUrl || MAPLIBRE_FALLBACK_STYLE)
  waitForStyleReady(mapInstance, onReady)
}

const fallbackToPublicMapStyle = (instance: Map | null, label: string) => {
  if (!instance || hasFallenBackToPublicMap.value) return
  hasFallenBackToPublicMap.value = true
  console.warn(`Falling back to public map style for ${label}`)
  instance.setStyle(MAPLIBRE_FALLBACK_STYLE)
}

const shouldFallbackFromMapError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false

  const candidate = error as {
    status?: number
    message?: string
    statusText?: string
  }

  if (candidate.status === 403) return true

  const message = `${candidate.message ?? ''} ${candidate.statusText ?? ''}`.toLowerCase()
  return (
    message.includes('accessdeniedexception') ||
    message.includes('not authorized') ||
    message.includes('403')
  )
}

const setMapColorScheme = (scheme: MapColorScheme) => {
  if (mapColorScheme.value === scheme) return
  mapColorScheme.value = scheme

  applyMapStyle(map.value, scheme, () => {
    applyMapCenterPadding(map.value, mapContainer.value)
    if (map.value && MapLibreLib && center.value) {
      if (marker.value) marker.value.remove()
      marker.value = new MapLibreLib.Marker()
        .setLngLat([center.value.lng, center.value.lat])
        .addTo(map.value)
    }
    updateCircle()
  })

  applyMapStyle(geoJsonMap.value, scheme, () => {
    applyMapCenterPadding(geoJsonMap.value, geoJsonMapContainer.value, GEOJSON_CENTER_VERTICAL_RATIO)
    updateGeoJsonLayer()
  })
}

const toggleMapColorScheme = () => {
  const nextScheme =
    mapColorScheme.value === MAP_COLOR_SCHEME_DARK ? MAP_COLOR_SCHEME_LIGHT : MAP_COLOR_SCHEME_DARK
  setMapColorScheme(nextScheme)
}

const attachAwsMapDebug = (instance: Map, label: string) => {
  const log = (event: string, _payload?: Record<string, unknown>) => {
    // Intentionally silent in production
  }

  instance.on('load', () => log('load'))
  instance.on('styledata', () => log('styledata'))
  instance.on('sourcedata', (event) =>
    log('sourcedata', {
      sourceId: event.sourceId,
      isSourceLoaded: event.isSourceLoaded,
      dataType: event.dataType,
      sourceDataType: event.sourceDataType
    })
  )
  instance.on('tiledata', (event) =>
    log('tiledata', {
      sourceId: event.sourceId,
      tile: event.tile ? { x: event.tile.x, y: event.tile.y, z: event.tile.z } : undefined
    })
  )
  instance.on('tileload', (event) =>
    log('tileload', {
      sourceId: event.sourceId,
      tile: event.tile ? { x: event.tile.x, y: event.tile.y, z: event.tile.z } : undefined
    })
  )
  instance.on('tileerror', (event) =>
    log('tileerror', {
      sourceId: event.sourceId,
      error: event.error
    })
  )
  instance.on('tileerror', (event) => {
    if (shouldFallbackFromMapError(event.error)) {
      fallbackToPublicMapStyle(instance, label)
    }
  })
  instance.on('error', (event) =>
    log('error', {
      error: event.error
    })
  )
  instance.on('error', (event) => {
    if (shouldFallbackFromMapError(event.error)) {
      fallbackToPublicMapStyle(instance, label)
    }
  })
}

const resolveApproximateCenter = async () => {
  try {
    const response = await fetch(GEO_IP_ENDPOINT, { method: 'GET' })
    if (!response.ok) throw new Error('GeoIP request failed')

    const data = (await response.json()) as { latitude?: number; longitude?: number }
    const lat = data.latitude
    const lng = data.longitude

    if (lat == null || lng == null || !isValidLatLng(lat, lng)) return

    approximateCenter.value = { lat, lng }

    if (map.value && !center.value) {
      map.value.setCenter([lng, lat])
    }
    if (geoJsonMap.value && !geoJsonData.value) {
      geoJsonMap.value.setCenter([lng, lat])
    }
  } catch (error) {
    console.warn('GeoIP lookup failed, using default center.', error)
  }
}

// Search State
/** User's current search string for finding locations in GeoJSON mode */
const searchQuery = ref('')

/** User's current search string for finding locations in radius mode */
const radiusSearchQuery = ref('')

/** List of location results found via Nominatim search */
const searchResults = ref<NominatimFeature[]>([])

/** Boolean flag indicating if a location search is currently in progress */
const isSearching = ref(false)

// Modal State
const showResultsModal = ref(false)
const modalResults = ref<UnifiedSearchResult[]>([])

watch(showResultsModal, (isOpen) => {
  if (isOpen) {
    uiStore.openModal()
  } else {
    uiStore.closeModal()
  }
})

const openSearchResultsModal = (results: NominatimFeature[]) => {
  modalResults.value = results.map((r) => ({
    id: r.place_id || Math.random(),
    name: r.display_name || '',
    type: r.properties?.type || 'location',
    raw: r
  }))
  showResultsModal.value = true
}

const runRadiusSearch = async (query: string) => {
  isSearching.value = true
  searchResults.value = []

  try {
    const results = await fetchGeoSearchResults(query)
    if (results.length > 0) {
      searchResults.value = results
      openSearchResultsModal(searchResults.value)
      return
    }

    uiStore.showError({
      title: 'No Locations Found',
      message: 'We couldn\'t find any locations matching your search.',
      description: 'Try a different search term or check the spelling.'
    });
  } catch (error) {
    console.error('Search error:', error)
    uiStore.showError({
      title: 'Search Error',
      message: 'Failed to search location.',
      description: 'Please try again later.'
    });
  } finally {
    isSearching.value = false
  }
}

const isGeoJsonGeometry = (value: unknown): value is GeoJSONGeometry => {
  return isRecord(value) && typeof value.type === 'string'
}

const isPolygonGeometry = (geometry: GeoJSONGeometry | null): geometry is GeoJSONGeometry => {
  if (!geometry) return false
  return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon'
}

const resolveGeometryFromSearchResult = (result: GeoSearchResult): GeoJSONGeometry | null => {
  if (isGeoJsonGeometry(result.geojson)) return result.geojson

  const lat = result.lat != null ? Number(result.lat) : NaN
  const lng = result.lon != null ? Number(result.lon) : NaN

  if (isValidLatLng(lat, lng)) {
    return {
      type: 'Point',
      coordinates: [lng, lat]
    }
  }

  return null
}

const mapGeoSearchResultToFeature = (result: GeoSearchResult): NominatimFeature | null => {
  const geometry = resolveGeometryFromSearchResult(result)
  if (!geometry) return null

  const displayName = resolveDisplayName(result)
  const normalizedPlaceId = normalizePlaceId(result.place_id)
  const normalizedOsmId = normalizeOsmId(result.osm_id)
  const normalizedOsmType = normalizeOptionalString(result.osm_type)
  const normalizedClass = normalizeOptionalString(result.class)
  const placeIdValue = normalizedPlaceId ?? DEFAULT_PLACE_ID

  return {
    type: 'Feature',
    geometry,
    display_name: displayName,
    place_id: placeIdValue,
    properties: {
      display_name: displayName,
      type: result.type || result.class || DEFAULT_LOCATION_TYPE,
      place_id: placeIdValue,
      ...(normalizedOsmId ? { osm_id: normalizedOsmId } : {}),
      ...(normalizedOsmType ? { osm_type: normalizedOsmType } : {}),
      ...(normalizedClass ? { class: normalizedClass } : {}),
      address: result.address
    }
  }
}

const fetchGeoSearchResults = async (
  query: string,
  options: { polygonOnly?: boolean } = {}
): Promise<NominatimFeature[]> => {
  const polygonOnly = Boolean(options.polygonOnly)
  const data = await ApiService.searchLocation(query, { polygonOnly })

  if (isGeoSearchResultArray(data)) {
    const features = data
      .map(mapGeoSearchResultToFeature)
      .filter((feature): feature is NominatimFeature => Boolean(feature))

    if (!polygonOnly) return features
    return features.filter((feature) => isPolygonGeometry(feature.geometry))
  }

  if (isGeoJsonFeatureCollection(data)) {
    const features = data.features
      .map(mapFeatureCollectionResult)
      .filter((feature): feature is NominatimFeature => Boolean(feature))

    if (!polygonOnly) return features
    return features.filter((feature) => isPolygonGeometry(feature.geometry))
  }

  throw new Error('Invalid geo search response')
}

/** Performs a location search using the geo search API for GeoJSON mode */
const searchLocation = async () => {
  if (!searchQuery.value.trim()) return

  isSearching.value = true
  searchResults.value = []

  try {
    const results = await fetchGeoSearchResults(searchQuery.value.trim(), { polygonOnly: true })
    if (!results.length) {
      uiStore.showError({
        title: 'No Locations Found',
        message: 'We couldn\'t find polygon geofence boundaries for that search.',
        description: 'Try a city, suburb, district, or region name instead of a specific address.'
      });
      return
    }

    searchResults.value = results
    openSearchResultsModal(searchResults.value)
  } catch (error) {
    console.error('Search error:', error)
    uiStore.showError({
      title: 'Search Error',
      message: 'Failed to search location.',
      description: 'Please try again later.'
    });
  } finally {
    isSearching.value = false
  }
}

/** Performs a location search using the Nominatim API for radius mode */
const searchRadiusLocation = async () => {
  await runRadiusSearch(radiusSearchQuery.value)
}

const handleModalSelect = async (unifiedResult: UnifiedSearchResult) => {
  showResultsModal.value = false
  const result = unifiedResult.raw as NominatimFeature
  if (locationType.value === 'radius') {
    selectRadiusLocation(result)
    return
  }

  await selectLocation(result)
}

const collectCoordinates = (geometry: GeoJSONGeometry): LngLat[] => {
  if (geometry.type === 'Point') return [geometry.coordinates as LngLat]
  if (geometry.type === 'LineString') return geometry.coordinates as LngLat[]
  if (geometry.type === 'Polygon') return geometry.coordinates.flat(1) as LngLat[]
  if (geometry.type === 'MultiPoint') return geometry.coordinates as LngLat[]
  if (geometry.type === 'MultiLineString') return geometry.coordinates.flat(1) as LngLat[]
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat(2) as LngLat[]
  if (geometry.type === 'GeometryCollection') {
    return geometry.geometries.flatMap((nested) => collectCoordinates(nested))
  }
  return []
}

const getFeatureCenter = (feature: GeoJSONFeature): { lat: number; lng: number } | null => {
  const coords = collectCoordinates(feature.geometry)
  if (coords.length === 0) return null

  const totals = coords.reduce(
    (acc, coord) => {
      const [lng, lat] = coord
      if (!isValidLatLng(lat, lng)) return acc
      return {
        lat: acc.lat + lat,
        lng: acc.lng + lng,
        count: acc.count + 1
      }
    },
    { lat: 0, lng: 0, count: 0 }
  )

  if (totals.count === 0) return null
  return {
    lat: totals.lat / totals.count,
    lng: totals.lng / totals.count
  }
}

const selectRadiusLocation = (result: NominatimFeature) => {
  const centerPoint = getFeatureCenter(result)
  if (!centerPoint) {
    uiStore.showError({
      title: 'Location Error',
      message: 'Selected location is missing coordinates.',
      description: 'Please try another result.'
    });
    return
  }

  selectedAddress.value = result.display_name || ''
  radiusSearchQuery.value = ''
  setCenter(centerPoint.lat, centerPoint.lng)
  map.value?.flyTo({
    center: [centerPoint.lng, centerPoint.lat],
    zoom: 15,
    padding: buildMapCenterPadding(mapContainer.value)
  })
}

const selectLocation = async (result: NominatimFeature) => {
  try {
    const rawProps = isRecord(result.properties) ? result.properties : undefined
    const normalizedOsmId = normalizeOsmId(rawProps?.osm_id ?? result.osm_id)
    const normalizedOsmType = normalizeOptionalString(rawProps?.osm_type ?? result.osm_type)
    const normalizedClass = normalizeOptionalString(rawProps?.class ?? result.class)
    const normalized = normalizeGeoJsonSelection(result, {
      display_name:
        typeof result.display_name === 'string'
          ? result.display_name
          : (rawProps?.display_name as string | undefined),
      type: typeof rawProps?.type === 'string' ? rawProps.type : DEFAULT_LOCATION_TYPE,
      place_id: result.place_id,
      osm_id: normalizedOsmId ?? undefined,
      osm_type: normalizedOsmType ?? undefined,
      class: normalizedClass ?? undefined
    })

    if (!normalized) {
      uiStore.showError({
        title: 'Geometry Error',
        message: 'Selected location does not include polygon geometry.',
        description: 'Try another result or use Radius mode.'
      });
      return
    }

    geoJsonData.value = normalized
    geoJsonFileName.value = result.display_name || DEFAULT_LOCATION_LABEL
    geoJsonFileSize.value = 0 // Not a file
    searchResults.value = [] // Clear results
    searchQuery.value = ''

    updateStore()
    await ensureGeoJsonMap()

    if (geoJsonMap.value) {
      if (geoJsonMap.value.isStyleLoaded()) {
        updateGeoJsonLayer()
      } else {
        geoJsonMap.value.once('load', updateGeoJsonLayer)
      }
    }
  } catch (err) {
    console.error(err)
    uiStore.showError({
      title: 'Selection Error',
      message: 'Failed to process selected location.',
      description: 'Please try again.'
    });
  }
}

const ensureGeoJsonMap = async () => {
  if (!geoJsonMapContainer.value) return

  if (!MapLibreLib) {
    MapLibreLib = maplibregl
  }

  if (geoJsonMap.value) {
    // If map exists, just trigger a resize and update
    geoJsonMap.value.resize()
    applyMapCenterPadding(geoJsonMap.value, geoJsonMapContainer.value, GEOJSON_CENTER_VERTICAL_RATIO)
    if (!geoJsonMap.value.isStyleLoaded()) {
      geoJsonMap.value.once('load', updateGeoJsonLayer)
    } else {
      updateGeoJsonLayer()
    }
    return
  }

  if (MapLibreLib) {
    const mapOptions = await resolveMapOptions()
    geoJsonMap.value = new MapLibreLib.Map({
      container: geoJsonMapContainer.value,
      center: [approximateCenter.value.lng, approximateCenter.value.lat],
      zoom: DEFAULT_MAP_ZOOM,
      scrollZoom: false,
      collectResourceTiming: false,
      ...mapOptions
    })
    applyMapCenterPadding(geoJsonMap.value, geoJsonMapContainer.value, GEOJSON_CENTER_VERTICAL_RATIO)
    attachAwsMapDebug(geoJsonMap.value, 'geojson')

    geoJsonMap.value.on('load', () => {
      const mapLibre = MapLibreLib
      if (!mapLibre) return
      if (!geoJsonNavControlAdded) {
        const NavControl = new mapLibre.NavigationControl({ showCompass: false })
        geoJsonMap.value?.addControl(NavControl, 'top-right')
        geoJsonNavControlAdded = true
      }
      updateGeoJsonLayer()
    })
  }
}

const resizeGeoJsonMap = () => {
  if (geoJsonMap.value) {
    geoJsonMap.value.resize()
    applyMapCenterPadding(geoJsonMap.value, geoJsonMapContainer.value, GEOJSON_CENTER_VERTICAL_RATIO)
  }
}

const extendBoundsWithFeature = (bounds: LngLatBounds, feature: GeoJSONFeature) => {
  if (!feature?.geometry) return

  if (feature.geometry.type === 'Polygon') {
    (feature.geometry.coordinates as LngLat[][]).forEach((ring: LngLat[]) => {
      ring.forEach((coord: LngLat) => bounds.extend(coord))
    })
  } else if (feature.geometry.type === 'MultiPolygon') {
    (feature.geometry.coordinates as LngLat[][][]).forEach((polygon: LngLat[][]) => {
      polygon.forEach((ring: LngLat[]) => {
        ring.forEach((coord: LngLat) => bounds.extend(coord))
      })
    })
  } else if (feature.geometry.type === 'Point') {
    bounds.extend(feature.geometry.coordinates as LngLat)
  }
}

const updateGeoJsonLayer = () => {
  if (!geoJsonMap.value || !MapLibreLib || !geoJsonMap.value.isStyleLoaded()) return

  const sourceId = 'selected-location'
  const fillId = 'selected-location-fill'
  const lineId = 'selected-location-line'

  if (geoJsonMap.value.getLayer(fillId)) geoJsonMap.value.removeLayer(fillId)
  if (geoJsonMap.value.getLayer(lineId)) geoJsonMap.value.removeLayer(lineId)
  
  const existingSource = geoJsonMap.value.getSource(sourceId)
  if (existingSource) geoJsonMap.value.removeSource(sourceId)

  if (!geoJsonData.value) return

  geoJsonMap.value.addSource(sourceId, {
    type: 'geojson',
    data: geoJsonData.value
  })

  geoJsonMap.value.addLayer({
    id: fillId,
    type: 'fill',
    source: sourceId,
    paint: {
      'fill-color': '#52C696',
      'fill-opacity': 0.3,
      'fill-outline-color': '#2E8B57'
    }
  })
  
  geoJsonMap.value.addLayer({
    id: lineId,
    type: 'line',
    source: sourceId,
    paint: {
      'line-color': '#2E8B57',
      'line-width': 2
    }
  })

  const bounds = new MapLibreLib.LngLatBounds()
  geoJsonData.value.features?.forEach((feature: GeoJSONFeature) => extendBoundsWithFeature(bounds, feature))

  if (!bounds.isEmpty()) {
    geoJsonMap.value.fitBounds(bounds, {
      padding: buildMapFitPadding(geoJsonMapContainer.value, GEOJSON_CENTER_VERTICAL_RATIO),
      maxZoom: 16
    })
  }
}

const setMode = (mode: LocationType) => {
  locationType.value = mode
  updateStore()
}

const initMap = async () => {
  if (!mapContainer.value) return

  if (map.value) {
    // If map already exists, just resize it and return
    map.value.resize()
    applyMapCenterPadding(map.value, mapContainer.value)
    return
  }

  MapLibreLib = maplibregl

  const mapOptions = await resolveMapOptions()
  map.value = new maplibregl.Map({
    container: mapContainer.value,
    center: [approximateCenter.value.lng, approximateCenter.value.lat],
    zoom: DEFAULT_MAP_ZOOM,
    scrollZoom: false,
    collectResourceTiming: false,
    ...mapOptions
  })
  applyMapCenterPadding(map.value, mapContainer.value)
  attachAwsMapDebug(map.value, 'radius')

  map.value.on('load', () => {
    // Add navigation controls (zoom +/-)
    if (!radiusNavControlAdded) {
      const NavControl = new maplibregl.NavigationControl({ showCompass: false })
      map.value?.addControl(NavControl, 'top-right')
      radiusNavControlAdded = true
    }
    // Move click handler inside load to ensure map is ready
    map.value?.on('click', (e: maplibregl.MapMouseEvent) => {
      setCenter(e.lngLat.lat, e.lngLat.lng)
    })

    // If we have stored state, restore it
    if (store.template.locationConfig?.radiusConfig) {
      const { lat, lng, radius: storedRadius } = store.template.locationConfig.radiusConfig
      setCenter(lat, lng)
      radius.value = storedRadius
      map.value?.flyTo({
        center: [lng, lat],
        zoom: 15,
        padding: buildMapCenterPadding(mapContainer.value)
      })
    }
  })
}

const setCenter = (lat: number, lng: number) => {
  center.value = { lat, lng }
  
  // Disable scroll zoom once a location is selected to allow page scrolling
  if (map.value) {
    map.value.scrollZoom.disable()
  }
  
  if (marker.value) marker.value.remove()

  if (MapLibreLib && map.value) {
    marker.value = new MapLibreLib.Marker()
      .setLngLat([lng, lat])
      .addTo(map.value)
  } else {
    marker.value = null
  }
    
  updateCircle()
  updateStore()

}

const updateCircle = () => {
  if (!map.value || !center.value) return

  const sourceId = 'radius-source'
  const layerId = 'radius-layer'

  if (map.value.getSource(sourceId)) {
    map.value.removeLayer(layerId)
    map.value.removeSource(sourceId)
  }

  // Create a GeoJSON circle
  const circleGeoJson = createGeoJSONCircle(center.value, radius.value / 1000) // km

  map.value.addSource(sourceId, {
    type: 'geojson',
    data: circleGeoJson
  })

  map.value.addLayer({
    id: layerId,
    type: 'fill',
    source: sourceId,
    paint: {
      'fill-color': '#52C696',
      'fill-opacity': 0.3,
      'fill-outline-color': '#2E8B57'
    }
  })
  fitMapToRadiusBounds()

  updateStore()
}

// Store Sync
const updateStore = () => {
  const config: LocationConfig = {
    type: locationType.value,
    radiusConfig: locationType.value === 'radius' && center.value ? {
      lat: center.value.lat,
      lng: center.value.lng,
      radius: radius.value,
      address: selectedAddress.value,
      boundingBox: getRadiusBoundingBox(center.value, radius.value)
    } : undefined,
    geoJsonConfig: locationType.value === 'geojson' && geoJsonData.value ? {
      fileName: geoJsonFileName.value,
      fileSize: geoJsonFileSize.value,
      data: geoJsonData.value,
      osmId: resolveGeoJsonOsmId(geoJsonData.value),
      osmType: resolveGeoJsonOsmType(geoJsonData.value),
      osmClass: resolveGeoJsonClass(geoJsonData.value)
    } : undefined
  }
  
  store.setLocationConfig(config)
  store.updateSectionCompletion()
}

// Helper to create circle polygon
const createGeoJSONCircle = (
  center: { lat: number, lng: number },
  radiusInKm: number
): GeoJSONFeatureCollection => {
  const points = 64
  const coords = { latitude: center.lat, longitude: center.lng }
  const km = radiusInKm
  const ret: LngLat[] = []
  const distanceX = km / (111.320 * Math.cos(coords.latitude * Math.PI / 180))
  const distanceY = km / 110.574

  let theta, x, y
  for (let i = 0; i < points; i++) {
    theta = (i / points) * (2 * Math.PI)
    x = distanceX * Math.cos(theta)
    y = distanceY * Math.sin(theta)
    ret.push([coords.longitude + x, coords.latitude + y])
  }
  ret.push(ret[0])

  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [ret]
      },
      properties: {}
    }]
  }
}

const getRadiusBoundingBox = (center: { lat: number, lng: number }, radiusInMeters: number) => {
  const radiusInKm = radiusInMeters / 1000
  const kmPerDegreeLat = 110.574
  const kmPerDegreeLngAtEquator = 111.320
  const latRadians = center.lat * Math.PI / 180
  const deltaLng = radiusInKm / (kmPerDegreeLngAtEquator * Math.cos(latRadians))
  const deltaLat = radiusInKm / kmPerDegreeLat

  return {
    north: center.lat + deltaLat,
    south: center.lat - deltaLat,
    east: center.lng + deltaLng,
    west: center.lng - deltaLng
  }
}

const buildRadiusBounds = (center: { lat: number, lng: number }, radiusInMeters: number) => {
  if (!MapLibreLib) return null

  const { north, south, east, west } = getRadiusBoundingBox(center, radiusInMeters)

  if ([north, south, east, west].some((value) => !Number.isFinite(value))) return null

  return new MapLibreLib.LngLatBounds([west, south], [east, north])
}

const fitMapToRadiusBounds = () => {
  if (!map.value || !center.value) return
  const bounds = buildRadiusBounds(center.value, radius.value)
  if (!bounds || bounds.isEmpty()) return
  map.value.fitBounds(bounds, { padding: buildMapFitPadding(mapContainer.value), maxZoom: 16 })
}

// Keep the slider reference in sync whenever an applied radius change happens elsewhere in the UI
watch(radius, (newRadius) => {
  rawSliderValue.value = radiusToSlider(newRadius)
})


watch(locationType, (newType) => {
  if (newType === 'radius') {
    setTimeout(initMap, 50)
  } else if (newType === 'geojson') {
    setTimeout(async () => {
      await ensureGeoJsonMap()
      updateGeoJsonLayer()
      resizeGeoJsonMap()
    }, 50)
  }
})

// Lifecycle
onMounted(() => {
  resolveApproximateCenter()
  if (store.template.locationConfig) {
    locationType.value = store.template.locationConfig.type
    
    // Restore address if it exists
    if (locationType.value === 'radius' && store.template.locationConfig.radiusConfig) {
      selectedAddress.value = store.template.locationConfig.radiusConfig.address || ''
      // Ensure restored radius is within our new bounds
      const restoredRadius = store.template.locationConfig.radiusConfig.radius
      radius.value = Math.max(RADIUS_MIN, Math.min(RADIUS_MAX, restoredRadius))
    }

    if (locationType.value === 'geojson' && store.template.locationConfig.geoJsonConfig) {
      const storedGeoJson = store.template.locationConfig.geoJsonConfig.data
      const normalized = normalizeGeoJsonCollection(storedGeoJson)
      if (normalized) {
        geoJsonData.value = normalized
      } else {
        uiStore.showError({
          title: 'Stored Geometry Error',
          message: 'Stored Geo Fence is missing polygon geometry.',
          description: 'You may need to re-select the location.'
        });
      }
      geoJsonFileName.value = store.template.locationConfig.geoJsonConfig.fileName
      geoJsonFileSize.value = store.template.locationConfig.geoJsonConfig.fileSize
    }
  }

  // Map initialization is handled by the watch(locationType) 
  // with an initial delay to ensure DOM is ready
  setTimeout(() => {
    if (locationType.value === 'radius') {
      initMap()
    } else {
      ensureGeoJsonMap().then(resizeGeoJsonMap)
    }
  }, 100)
})

onUnmounted(() => {
  if (map.value) {
    map.value.remove()
    map.value = null
  }
  if (geoJsonMap.value) {
    geoJsonMap.value.remove()
    geoJsonMap.value = null
  }
  radiusNavControlAdded = false
  geoJsonNavControlAdded = false
})
</script>

<style scoped>
.location-section {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  gap: 0;
  padding: 0;
  position: relative;
  isolation: isolate;
}

.location-map-theme-toggle {
  position: absolute;
  top: clamp(0.75rem, 2vw, 1.25rem);
  right: var(--base-layout-panel-padding);
  z-index: 14;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  background: var(--color-background-soft);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  cursor: pointer;
}

.location-map-theme-toggle::before {
  content: '';
  width: 16px;
  height: 16px;
  display: block;
  margin: 0 auto;
  background-color: var(--vt-c-accent);
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: 16px 16px;
}

.location-map-theme-toggle[data-scheme='Dark']::before {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M21 14.5A9 9 0 0 1 9.5 3a7 7 0 1 0 11.5 11.5Z'/%3E%3C/svg%3E");
}

.location-map-theme-toggle[data-scheme='Light']::before {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='m12 17.27-5.18 3.73 1.64-6.03L3 9.24l6.19-.47L12 3l2.81 5.77 6.19.47-5.46 5.73 1.64 6.03z'/%3E%3C/svg%3E");
}

.location-section > * {
  max-width: none;
}

.location-section :deep(.section-header),
.location-content {
  margin-inline: 0;
  width: 100%;
}

.location-content {
  width: 100%;
  max-width: none;
  --location-content-padding-inline: var(--base-layout-panel-padding);
  --location-content-padding-block: var(--section-padding-block-start);
  --location-content-padding-bottom: var(--space-xl);
  --location-block-gap: var(--section-content-gap);
  --location-map-min-height: 200px;
  --location-map-ideal-height: calc(var(--section-available-height, 100vh) * 0.34);
  --location-map-max-height: 300px;
  --location-preview-min-height: 200px;
  --location-preview-ideal-height: 34vh;
  --location-preview-max-height: 280px;
  --location-upload-min-height: 120px;
  --location-upload-ideal-height: 20vh;
  --location-upload-max-height: 180px;
  /* Keep search inputs readable on wide screens. */
  --location-search-container-max-width: min(100%, clamp(24rem, 70vw, 56rem));
  --location-search-group-max-width: min(100%, clamp(20rem, 60vw, 42rem));
  padding: var(--location-content-padding-block) var(--location-content-padding-inline)
    var(--location-content-padding-bottom);
  display: flex;
  flex-direction: column;
  min-height: auto;
  --location-viewport-reserve: clamp(3rem, calc(var(--section-available-height, 100vh) * 0.1), 9rem);
  max-height: none;
}

.mode-toggle {
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
  margin-bottom: var(--location-block-gap);
  pointer-events: auto;
  background: transparent;
  padding: 0;
  border-radius: 0;
  backdrop-filter: none;
}

.zone-picker-label {
  position: absolute;
  top: -0.72rem;
  left: 0;
  margin: 0;
  padding: 0.2rem 0.7rem;
  border-radius: 999px;
  background: var(--vt-c-accent);
  color: #0b1220;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: 0.02em;
  pointer-events: none;
}

.toggle-btn {
  /* deselected: dark blue */
  padding: 0.45rem 0.9rem;
  border-radius: 12px;
  border: 2px solid var(--vt-c-accent);
  background: var(--vt-c-background-primary);
  color: var(--vt-c-accent);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.toggle-btn.active {
  /* selected: solid pastel green */
  background: var(--vt-c-accent);
  border-color: var(--vt-c-accent);
  color: var(--vt-c-background-primary);
}

/* Radius Mode */
.radius-mode {
  display: flex;
  flex-direction: column;
  gap: var(--location-block-gap);
  align-items: flex-start;
  pointer-events: none;
}

.location-section-background {
  position: absolute;
  inset: 0;
  border-radius: 0;
  border: none;
  background: var(--color-background-soft);
  overflow: hidden;
  z-index: 0;
}

.location-map-layer {
  position: absolute;
  inset: 0;
}

.location-section :deep(.section-header) {
  position: relative;
  z-index: 2;
  padding-inline: var(--base-layout-section-padding-inline);
  padding-block-start: clamp(0.75rem, 2vw, 1.5rem);
}

.location-section :deep(.section-header h3) {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.75rem;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  backdrop-filter: blur(6px);
}


.location-overlay {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: var(--location-block-gap);
  pointer-events: none;
  align-items: flex-start;
  transform: translateY(0.75rem);
}

.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  z-index: 10;
  border-radius: 16px;
  color: white;
  font-weight: 600;
  backdrop-filter: blur(2px);
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.map-container {
  width: 100%;
  height: 100%;
}

.controls {
  background: transparent;
  padding: 0;
  border-radius: 0;
}

.map-controls--radius {
  pointer-events: auto;
  z-index: 10;
}

.controls--vertical {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: clamp(7rem, 15vw, 10rem);
  padding: 0.5rem 0;
  pointer-events: auto;
}

.control-group {
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  pointer-events: auto;
}

.radius-display {
  background: rgba(20, 20, 20, 0.9);
  backdrop-filter: blur(12px);
  padding: 0.4rem 0.7rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1.5px solid var(--vt-c-accent);
  color: var(--vt-c-accent);
  font-family: 'Monaco', 'Consolas', monospace;
  font-weight: 700;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  z-index: 5;
  min-width: 85px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.radius-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  opacity: 0.8;
  letter-spacing: 0.1em;
}

.radius-value {
  font-size: 1rem;
  letter-spacing: 0.05em;
}

.slider-container--vertical {
  position: relative;
  height: clamp(10rem, 25vh, 16rem);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-left: -1.5rem;
  pointer-events: auto;
}

.radius-slider {
  width: 20px;
  height: 100%;
  -webkit-appearance: slider-vertical;
  appearance: slider-vertical;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  cursor: pointer;
  z-index: 2;
  margin: 0;
  outline: none;
  transition: all 0.2s ease;
  pointer-events: auto;
  touch-action: none; /* Critical for mobile drag */
}

.radius-slider:hover {
  background: rgba(255, 255, 255, 0.25);
}

.radius-notches {
  position: absolute;
  left: calc(50% + 18px);
  top: 0;
  bottom: 0;
  right: -50px;
  pointer-events: none;
}

.radius-notch {
  position: absolute;
  left: 0;
  display: flex;
  align-items: center;
  pointer-events: auto;
  cursor: pointer;
  transform: translateY(50%);
  height: 20px; /* Increase hit area */
}

.radius-notch:hover .notch-label {
  color: var(--vt-c-accent);
  transform: scale(1.1);
}

.notch-line {
  width: 10px;
  height: 2px;
  background: rgba(255, 255, 255, 0.4);
  margin-right: 8px;
  border-radius: 1px;
}

.notch-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 700;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.value-display {
  font-weight: bold;
  min-width: 60px;
}

/* GeoJSON Mode */
.geojson-mode {
  display: flex;
  flex-direction: column;
  gap: var(--location-block-gap);
  align-items: flex-start;
  pointer-events: none;
}

.map-controls {
  align-self: flex-start;
  z-index: 2;
}

.radius-mode .search-container,
.radius-mode .map-controls,
.geojson-mode .search-container,
.geojson-mode .error-message {
  pointer-events: auto;
}

.upload-area {
  width: 100%;
  height: clamp(
    var(--location-upload-min-height),
    var(--location-upload-ideal-height),
    var(--location-upload-max-height)
  );
  border: 2px dashed var(--color-border);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.upload-area:hover {
  border-color: var(--vt-c-accent);
  background: rgba(82, 198, 150, 0.05);
}

.search-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--location-block-gap);
  padding: 0.75rem;
  border-radius: 12px;
}

.search-container.radius-search {
  width: 90vw;
  max-width: none;
  margin: 0 auto;
  align-self: center;
  padding-inline: 0.75rem;
}

.radius-search .search-input-group {
  flex-direction: column;
  align-items: center;
  width: var(--location-search-group-max-width);
  margin-inline: auto;
}

.radius-search .search-input,
.radius-search .search-btn {
  width: 100%;
}

.search-container.geojson-search {
  width: 90vw;
  max-width: none;
  margin: 0 auto;
  align-self: center;
  padding-inline: 0.75rem;
}

@media (min-width: 1024px) {
  .search-container.radius-search,
  .search-container.geojson-search {
    /* Wider desktop search container for readability. */
    width: 90%;
    margin-inline: auto;
    align-self: center;
    padding-inline: 0;
  }
}

.geojson-search .search-input-group {
  flex-direction: column;
  align-items: center;
  width: var(--location-search-group-max-width);
  margin-inline: auto;
}

.geojson-search .search-input,
.geojson-search .search-btn {
  width: 100%;
}

.search-input-group {
  display: flex;
  gap: 0.5rem;
  width: 100%;
}

.search-input-group--active .search-input {
  border-color: var(--vt-c-accent);
  box-shadow: 0 0 0 2px rgba(var(--vt-c-accent-rgb), 0.15);
}

.search-input {
  flex: 1;
  padding: clamp(0.55rem, 1.6vw, 0.75rem) clamp(0.75rem, 2vw, 1rem);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  background: var(--color-background-mute);
  color: var(--color-text);
  font-size: 1rem;
}

.search-btn {
  padding: 0 clamp(0.9rem, 2vw, 1.25rem);
  background: var(--vt-c-accent);
  border: none;
  border-radius: 12px;
  color: #1a1a1a;
  font-weight: 600;
  cursor: pointer;
}

.divider {
  width: 100%;
  text-align: center;
  position: relative;
  margin: 1rem 0;
}

.divider::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 100%;
  height: 1px;
  background: var(--color-border);
}

.divider span {
  background: var(--color-background-soft);
  padding: 0 1rem;
  position: relative;
  color: var(--color-text);
  font-weight: 600;
  font-size: 0.9rem;
}

.geojson-map-preview {
  width: 100%;
  height: clamp(
    var(--location-preview-min-height),
    var(--location-preview-ideal-height),
    var(--location-preview-max-height)
  );
  border-radius: 16px;
  overflow: hidden;
  margin-top: 1rem;
  border: 1px solid var(--color-border);
}

.upload-placeholder {
  text-align: center;
}

.upload-icon {
  font-size: 2.5rem;
  display: block;
  margin-bottom: 0.5rem;
}

.file-info {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: space-between;
}

.remove-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  line-height: 1;
}

.error-message {
  color: #ff4757;
  font-size: 0.9rem;
}

/* MapLibre navigation control styling */
:deep(.maplibregl-ctrl-top-right) {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  margin: 0;
  z-index: 12;
}

:deep(.maplibregl-ctrl-top-right .maplibregl-ctrl-group) {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  background: transparent;
  box-shadow: none;
}

:deep(.maplibregl-ctrl-top-right .maplibregl-ctrl-group button) {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  background: var(--color-background-soft);
  color: var(--color-text);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
}

:deep(.maplibregl-ctrl-top-right .maplibregl-ctrl-group .maplibregl-ctrl-icon) {
  background-image: none;
  background-color: var(--vt-c-accent);
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: 16px 16px;
}

:deep(.maplibregl-ctrl-zoom-in .maplibregl-ctrl-icon) {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M11 5h2v14h-2zM5 11h14v2H5z'/%3E%3C/svg%3E");
}

:deep(.maplibregl-ctrl-zoom-out .maplibregl-ctrl-icon) {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M5 11h14v2H5z'/%3E%3C/svg%3E");
}

:deep(.maplibregl-ctrl-top-right .maplibregl-ctrl-group button:hover) {
  border-color: var(--vt-c-accent);
  background: var(--color-background-mute);
}

:deep(.maplibregl-ctrl-top-right .maplibregl-ctrl-group button:active) {
  transform: translateY(1px);
}


@media (max-width: 1024px) {
  .location-content {
    max-height: none;
  }
}

@media (max-width: 768px) {
  .location-section {
    --location-search-button-min-height: 2.25rem;
    --location-search-button-margin-top: 0;
  }

  .location-section {
    height: 100%;
    gap: 0;
  }

  .location-map-theme-toggle {
    top: 0.65rem;
    right: 0.75rem;
    width: 28px;
    height: 28px;
  }

  .location-map-theme-toggle::before {
    width: 14px;
    height: 14px;
    mask-size: 14px 14px;
  }

  .location-content {
    padding: 0.75rem;
    max-height: none;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
    --location-map-min-height: 170px;
    --location-map-ideal-height: 28vh;
    --location-map-max-height: 230px;
    --location-preview-min-height: 170px;
    --location-preview-ideal-height: 28vh;
    --location-preview-max-height: 230px;
    --location-upload-min-height: 90px;
    --location-upload-ideal-height: 16vh;
    --location-upload-max-height: 130px;
  }

  :deep(.section-header) {
    margin-bottom: 0.025rem;
  }

  :deep(.section-header h3) {
    font-size: 1.25rem;
    margin-bottom: 0.0125rem;
  }

  :deep(.section-header p) {
    font-size: 0.8rem;
    margin-bottom: 0;
  }

  .mode-toggle {
    margin-bottom: 0.5rem;
    gap: 0.25rem;
    padding: 0.25rem;
    border-radius: 8px;
    align-self: center;
    justify-content: center;
    margin-inline: auto;
  }

  .zone-picker-label {
    top: -0.62rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.82rem;
    padding: 0.18rem 0.58rem;
  }

  .location-overlay {
    transform: translateY(0.58rem);
  }
  
  .toggle-btn {
    padding: 0.25rem 0.45rem;
    font-size: 0.7rem;
    border-radius: 6px;
    min-width: 4.25rem;
    text-align: center;
  }

  .radius-mode,
  .geojson-mode {
    gap: 0.5rem;
  }

  .slider-container--vertical {
    height: clamp(8rem, 20vh, 12rem);
    margin-left: -1rem;
  }

  .radius-display {
    margin-bottom: 0.75rem;
    padding: 0.3rem 0.6rem;
    min-width: 75px;
  }

  .radius-value {
    font-size: 0.9rem;
  }

  .notch-label {
    font-size: 0.65rem;
  }

  .upload-area {
    height: clamp(
      var(--location-upload-min-height),
      var(--location-upload-ideal-height),
      var(--location-upload-max-height)
    );
  }

  .controls {
    padding: 0.5rem;
  }
  
  .control-group {
    margin-bottom: 0.25rem;
  }

  .search-input {
    padding: 0.4rem 0.6rem;
    font-size: 1rem;
  }

  .search-btn {
    height: auto;
    min-height: var(--location-search-button-min-height);
    margin-top: var(--location-search-button-margin-top);
    padding: 0.35rem 0.75rem;
    font-size: 0.85rem;
  }

  .search-input-group {
    gap: 0.35rem;
  }

  .search-container.radius-search {
    width: 90vw;
    max-width: none;
    margin: 0 auto;
    align-self: center;
    padding-inline: 0.75rem;
  }

  .search-container {
    padding: 0.6rem;
    border-radius: 10px;
    width: 100%;
  }

  .search-container.geojson-search {
    width: 90vw;
    max-width: none;
    margin: 0 auto;
    align-self: center;
    padding-inline: 0.75rem;
  }

  .search-input-group,
  .search-input,
  .search-btn {
    width: 100%;
  }

  :deep(.maplibregl-ctrl-top-right) {
    display: none;
  }
}
</style>
